/**
 * ENG-2260 Phase 4.6.E: buildHouseholdUrl
 *
 * Constructs the cert-level household URL for the new v2 household page.
 * Replaces the legacy /household/:household_id URL pattern.
 *
 * The CertHousehold is resolved server-side from the cert ID — callers
 * never need to know the cert_household_id.
 */

export function buildHouseholdUrl(
  propertySlug: string,
  options: { certificationId: number; edit?: boolean }
): string {
  const base = `/workbench/${propertySlug}/${options.certificationId}/household`;
  return options.edit ? `${base}/edit` : base;
}

/** Resolve cert id for household links when active_certification_id is absent. */
export function resolveHouseholdLinkCertificationId(
  activeCertificationId?: number | null,
  fallbackCertificationId?: number | null
): number | undefined {
  return activeCertificationId ?? fallbackCertificationId ?? undefined;
}

/** Build v2 cert-level household URL. */
export function buildComplianceHouseholdPath({
  propertySlug,
  householdId,
  activeCertificationId,
  fallbackCertificationId,
}: {
  propertySlug: string;
  householdId?: number | null;
  activeCertificationId?: number | null;
  fallbackCertificationId?: number | null;
}): string {
  const certificationId = resolveHouseholdLinkCertificationId(
    activeCertificationId,
    fallbackCertificationId
  );
  if (certificationId) {
    return buildHouseholdUrl(propertySlug, { certificationId });
  }
  return `/workbench/${propertySlug}/household/${householdId ?? ''}`;
}

/** Prefer cert/URL slug over stale Redux selectedProperty when building cert links. */
export function resolveCertPropertySlug({
  certificationPropertySlug,
  urlPropertySlug,
  selectedPropertySlug,
}: {
  certificationPropertySlug?: string | null;
  urlPropertySlug?: string | null;
  selectedPropertySlug?: string | null;
}): string {
  return (
    certificationPropertySlug ??
    urlPropertySlug ??
    selectedPropertySlug ??
    'undefined'
  );
}
